import VintedClient, {
  NotLoggedError,
  VintedItemUnavailableError,
  extractVintedItemPagePayloads,
} from "./VintedClient";
import {
  annotateVintedInventoryPageFailure,
  type VintedPageFailureContext,
} from "./vintedPageFailures";

/**
 * Scrape each wardrobe listing's item page for category/brand/description.
 *
 * A listing page can be gone while the wardrobe API still returns the row
 * (hidden, draft, processing, retired). That is an item-level condition: keep
 * the row, annotate it with `pageFailure`, and let the import continue. Only
 * auth failures and unexpected errors abort the run.
 */
export async function enrichInventoryItemsWithPageDetails(
  client: VintedClient,
  items: Record<string, unknown>[],
  context: VintedPageFailureContext
) {
  const results: Record<string, unknown>[] = [];

  for (const item of items) {
    // Anchor relative upload_date strings (e.g. "an hour ago") to scrape time.
    // Mapped later by uploadTransform (daily) / backend mapInventoryItem (direct).
    const requestDate = Date.now();
    const itemUrl = item.url;
    if (typeof itemUrl !== "string" || !itemUrl) {
      results.push({ ...item, requestDate });
      continue;
    }

    try {
      const html = await client.getItemPage(itemUrl);
      const details = extractVintedItemPagePayloads(html);
      results.push({ ...item, details, requestDate });
    } catch (e) {
      if (e instanceof NotLoggedError) throw e;
      if (e instanceof VintedItemUnavailableError) {
        console.warn("[Vinted] Item page unavailable, keeping row", {
          listingId: item.id,
          itemUrl,
          status: e.status,
        });
        results.push({
          ...annotateVintedInventoryPageFailure(item, e, context),
          requestDate,
        });
        continue;
      }
      throw e;
    }
  }

  return results;
}
